import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  limit,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { z } from 'zod';
import { db, functions } from '@/firebase/config';
import type { User, UserRole, ID } from '@shared/types';
import type { Timestamp } from 'firebase/firestore';
import { writeAuditLog } from '@shared/utils';

const newUserSchema = z.object({
  uid: z.string().min(1),
  email: z.string().email(),
  displayName: z.string().min(1),
});

type NewUserInput = z.infer<typeof newUserSchema>;

function getBootstrapAdminEmails(): string[] {
  const raw = import.meta.env.VITE_ADMIN_EMAILS ?? '';
  return raw
    .split(',')
    .map((e: string) => e.trim().toLowerCase())
    .filter((e: string) => e.length > 0);
}

export function isBootstrapAdmin(email: string | null | undefined): boolean {
  if (!email) return false;
  return getBootstrapAdminEmails().includes(email.toLowerCase());
}

export async function fetchUser(uid: string): Promise<User | null> {
  try {
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as User;
  } catch {
    return null;
  }
}

export async function createUserIfNotExists(
  uid: string,
  email: string,
  displayName: string
): Promise<User> {
  const validated = newUserSchema.parse({ uid, email, displayName } satisfies NewUserInput);

  const ref = doc(db, 'users', validated.uid);
  const snap = await getDoc(ref);

  // Firestoreルールにより users コレクションへの create はサーバー側 (onAuthUserCreate) のみ許可。
  // 既存ドキュメントがあればそのまま返す。なければ呼び出し側で fallback ユーザーを構築する。
  if (snap.exists()) {
    return { id: snap.id, ...snap.data() } as User;
  }

  const now = serverTimestamp() as unknown as Timestamp;
  const newUser: Omit<User, 'id'> = {
    email: validated.email,
    displayName: validated.displayName,
    role: 'general',
    departmentId: null,
    organizationId: null,
    photoURL: null,
    mfaEnabled: false,
    lastLoginAt: null,
    isActive: true,
    createdAt: now,
    updatedAt: now,
    createdBy: validated.uid,
    updatedBy: validated.uid,
    isDeleted: false,
  };

  await setDoc(ref, newUser);
  return { id: validated.uid, ...newUser };
}

// モジュールレベルキャッシュ（5分TTL）
let _usersCache: User[] | null = null;
let _usersCacheTs = 0;
const USERS_CACHE_TTL = 5 * 60 * 1000;

export function invalidateUsersCache(): void {
  _usersCache = null;
}

export async function fetchAllUsers(): Promise<User[]> {
  if (_usersCache && Date.now() - _usersCacheTs < USERS_CACHE_TTL) {
    return _usersCache;
  }
  try {
    const ref = collection(db, 'users');
    const q = query(ref, where('isDeleted', '==', false), orderBy('createdAt'), limit(200));
    const snap = await getDocs(q);
    const users = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as User);
    _usersCache = users;
    _usersCacheTs = Date.now();
    return _usersCache;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`ユーザー一覧の取得に失敗しました: ${msg}`);
  }
}

export async function updateUserRole(uid: ID, role: UserRole, adminId: ID): Promise<void> {
  try {
    const ref = doc(db, 'users', uid);
    await updateDoc(ref, {
      role,
      updatedAt: serverTimestamp(),
      updatedBy: adminId,
    });
    await writeAuditLog({ entityType: 'user', entityId: uid, action: 'update', userId: adminId, after: { role } });
    invalidateUsersCache();
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`ロールの更新に失敗しました: ${msg}`);
  }
}

export async function toggleUserActive(uid: ID, isActive: boolean, adminId: ID): Promise<void> {
  try {
    const ref = doc(db, 'users', uid);
    await updateDoc(ref, {
      isActive,
      updatedAt: serverTimestamp(),
      updatedBy: adminId,
    });
    await writeAuditLog({ entityType: 'user', entityId: uid, action: 'update', userId: adminId, after: { isActive } });
    invalidateUsersCache();
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`ユーザー状態の更新に失敗しました: ${msg}`);
  }
}

export async function assignUserOrganization(
  uid: ID,
  organizationId: ID | null,
  adminId: ID
): Promise<void> {
  try {
    const ref = doc(db, 'users', uid);
    await updateDoc(ref, {
      organizationId,
      updatedAt: serverTimestamp(),
      updatedBy: adminId,
    });
    await writeAuditLog({ entityType: 'user', entityId: uid, action: 'update', userId: adminId, after: { organizationId } });
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`ユーザーへの組織割り当てに失敗しました: ${msg}`);
  }
}

export interface AdminCreateUserInput {
  email: string;
  displayName: string;
  role: UserRole;
  password?: string;
}

export interface AdminCreateUserResult {
  uid: string;
  temporaryPassword: string;
  resetLink: string;
}

export interface AdminUpdateUserInput {
  uid: ID;
  email?: string;
  displayName: string;
  role: UserRole;
}

export async function adminCreateUser(input: AdminCreateUserInput): Promise<AdminCreateUserResult> {
  try {
    const fn = httpsCallable<AdminCreateUserInput, AdminCreateUserResult>(
      functions,
      'createUserByAdmin'
    );
    const result = await fn(input);
    return result.data;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`ユーザーの作成に失敗しました: ${msg}`);
  }
}

export async function adminUpdateUser(input: AdminUpdateUserInput): Promise<void> {
  try {
    const fn = httpsCallable<AdminUpdateUserInput, { uid: string }>(functions, 'updateUserByAdmin');
    await fn(input);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`ユーザーの更新に失敗しました: ${msg}`);
  }
}

export async function adminDeleteUser(uid: ID): Promise<void> {
  try {
    const fn = httpsCallable<{ uid: string }, { uid: string }>(functions, 'deleteUserByAdmin');
    await fn({ uid });
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`ユーザーの削除に失敗しました: ${msg}`);
  }
}
